(() => {
  const initialize = async (root) => {
    if (!root || root.dataset.erInitialized === 'true') return;
    root.dataset.erInitialized = 'true';
    const controller = new AbortController();
    let disposed = false;
    const labelNode = root.querySelector('[data-er-labels]');
    const labels = labelNode ? JSON.parse(labelNode.textContent) : {};
    const label = (name) => labels[name] || name;
    requestAnimationFrame(() => root.classList.add('is-page-ready'));
    root.addEventListener('aura:music:dispose', () => {disposed = true;controller.abort();delete root.dataset.erInitialized;}, {once:true});
    const unload = (event) => {
      if (String(event.detail?.sectionId) === root.dataset.sectionId) root.dispatchEvent(new Event('aura:music:dispose'));
    };
    document.addEventListener('shopify:section:unload', unload, {signal:controller.signal});

    const results = root.querySelector('[data-er-results]');
    if (!results) {
      requestAnimationFrame(() => root.classList.add('is-page-ready'));
      return;
    }

    const PREVIEW_LIMIT_SECONDS = 60;


    const paginationState = root.querySelector('[data-er-pagination-state]');
    const paginationShell = root.querySelector('[data-er-pagination-shell]');
    const hydrateCatalog = async () => {
      let nextUrl = paginationState?.dataset.nextUrl || '';
      if (!nextUrl || typeof fetch !== 'function' || typeof DOMParser === 'undefined') {
        if (paginationShell) {
          const note = paginationShell.querySelector('.erdisc__pagination-note');
          if (note) note.textContent = '';
        }
        return;
      }

      const seenProducts = new Set(
        [...root.querySelectorAll('[data-er-release]')].map((card) => card.dataset.productId).filter(Boolean)
      );
      let pageGuard = 0;

      try {
        const visited = new Set();
        while (nextUrl && pageGuard < 100) {
          pageGuard += 1;
          const url = new URL(nextUrl, window.location.origin);
          url.searchParams.set('section_id', root.dataset.sectionId);
          if (url.origin !== location.origin || visited.has(url.href)) throw new Error('Invalid catalog page');
          visited.add(url.href);

          const response = await fetch(url.toString(), {
            signal: AbortSignal.any([controller.signal, AbortSignal.timeout(20000)]),
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });
          if (!response.ok) throw new Error(`Catalog page ${pageGuard + 1} returned ${response.status}`);

          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const nextRoot = documentFragment.getElementById(root.id);
          const nextResults = nextRoot?.querySelector('[data-er-results]');
          if (!nextResults) throw new Error('Catalog section response did not include release rows.');

          [...nextResults.querySelectorAll('[data-er-release]')].forEach((card) => {
            const productId = card.dataset.productId || '';
            if (productId && seenProducts.has(productId)) return;
            if (productId) seenProducts.add(productId);
            card.hidden = true;
            results.appendChild(card);
          });

          refreshCatalog();
          const nextState = nextRoot.querySelector('[data-er-pagination-state]');
          nextUrl = nextState?.dataset.nextUrl || '';
        }

        if (!nextUrl) {
          if (paginationShell) paginationShell.hidden = true;
          if (paginationState) paginationState.dataset.erCatalogComplete = 'true';
          buildTopArtists();
        }
        if (nextUrl && paginationShell) {
          const link = paginationShell.querySelector('a');
          if (link) link.href = nextUrl;
          const note = paginationShell.querySelector('.erdisc__pagination-note');
          if (note) note.textContent = 'More catalog pages are available below.';
        }
      } catch (error) {
        if (disposed) return;
        console.warn('East Rock Music: complete catalog hydration fell back to native pagination.', error);
        if (paginationShell) {
          paginationShell.hidden = false;
          const links = paginationShell.querySelector('nav');
          if (links && nextUrl) {const link=document.createElement('a');link.href=nextUrl;link.textContent=label('more');links.replaceChildren(link);}
          const note = paginationShell.querySelector('.erdisc__pagination-note');
          if (note) note.textContent = '';
        }
      }
    };

    if (disposed || !root.isConnected) return;

    const cards = [...results.querySelectorAll('[data-er-release]')];
    const upcomingSection = root.querySelector('[data-er-upcoming]');
    const upcomingResults = root.querySelector('[data-er-upcoming-results]');
    const upcomingCount = root.querySelector('[data-er-upcoming-count]');

    const localDateKey = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const todayKey = root.dataset.today || localDateKey();
    const isUpcomingCard = (card) => Boolean(card?.dataset.date && card.dataset.date > todayKey);
    const upcomingCards = cards.filter(isUpcomingCard);
    const releasedCards = cards.filter((card) => !isUpcomingCard(card));

    const search = root.querySelector('[data-er-search]');
    const artistFilter = root.querySelector('[data-er-artist-filter]');
    const typeFilter = root.querySelector('[data-er-type-filter]');
    const genreFilter = root.querySelector('[data-er-genre-filter]');
    const yearSelect = root.querySelector('[data-er-year]');
    const sortSelect = root.querySelector('[data-er-sort]');
    const filterToggle = root.querySelector('[data-er-filter-toggle]');
    const filterPanel = root.querySelector('[data-er-filter-panel]');
    const filterCount = root.querySelector('[data-er-filter-count]');
    const clearButton = root.querySelector('[data-er-clear]');
    const countEls = [...root.querySelectorAll('[data-er-count]')];
    const activeNote = root.querySelector('[data-er-active-note]');
    const empty = root.querySelector('[data-er-empty]');
    const emptyReset = root.querySelector('[data-er-empty-reset]');

    const now = root.querySelector('[data-er-now-playing]');
    const heroTitle = root.querySelector('[data-er-hero-title]');
    const heroArtist = root.querySelector('[data-er-hero-artist]');
    const heroFeatured = root.querySelector('[data-er-hero-featured]');
    const heroMeta = root.querySelector('[data-er-hero-meta]');
    const heroArt = root.querySelector('[data-er-hero-art]');
    const heroProductLinks = [...root.querySelectorAll('[data-er-hero-product-link]')];
    const heroStream = root.querySelector('[data-er-hero-stream]');
    const heroToggle = root.querySelector('[data-er-hero-toggle]');
    const heroSeek = root.querySelector('[data-er-hero-seek]');
    const heroTime = root.querySelector('[data-er-player-time]');
    const heroState = root.querySelector('[data-er-player-state]');
    const ambientA = root.querySelector('[data-er-ambient-a]');
    const ambientB = root.querySelector('[data-er-ambient-b]');
    const charts = root.querySelector('[data-er-charts]');
    const chartList = root.querySelector('[data-er-chart-list]');
    const chartTabs = [...root.querySelectorAll('[data-er-chart-tab]')];
    const summaryReleases = root.querySelector('[data-er-summary-releases]');
    const summaryArtists = root.querySelector('[data-er-summary-artists]');
    const summaryUpcoming = root.querySelector('[data-er-summary-upcoming]');

    const normalize = (value) => (value || '').toString().trim().toLowerCase();
    const displayValue = (value) => (value || '').toString().trim();
    const numericValue = (value) => {
      const parsed = Number(String(value ?? '').replace(/,/g, '').trim());
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    };
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const luminance = ([r, g, b]) => (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
    const mix = (from, to, amount) => [
      Math.round(from[0] + ((to[0] - from[0]) * amount)),
      Math.round(from[1] + ((to[1] - from[1]) * amount)),
      Math.round(from[2] + ((to[2] - from[2]) * amount))
    ];
    const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let rowRevealObserver = null;
    let paletteObserver = null;

    if (!reduceMotion && 'IntersectionObserver' in window) {
      rowRevealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          rowRevealObserver.unobserve(entry.target);
        });
      }, {
        root: null,
        rootMargin: '0px 0px -5% 0px',
        threshold: 0.06
      });
    }

    const prepareRowReveal = (card, index = 0) => {
      if (!card || card.hidden) return;
      card.style.setProperty('--er-reveal-delay', `${Math.min(index, 7) * 38}ms`);

      if (reduceMotion || !rowRevealObserver) {
        card.classList.add('is-revealed');
        card.dataset.erRevealReady = 'true';
        return;
      }

      if (card.dataset.erRevealReady === 'true' && card.classList.contains('is-revealed')) return;

      card.classList.remove('is-revealed');
      rowRevealObserver.unobserve(card);
      rowRevealObserver.observe(card);
      card.dataset.erRevealReady = 'true';
    };


    const themeVariableNames = ['--er-accent', '--er-accent-2', '--er-fg', '--er-muted', '--er-highlight'];

    if (now && now.parentElement !== document.body) {
      const staleDock = document.querySelector('#' + CSS.escape('ERDiscographyDock-' + root.dataset.sectionId) + '[data-er-portal="true"]');
      if (staleDock && staleDock !== now) staleDock.remove();

      const rootStyles = window.getComputedStyle(root);
      themeVariableNames.forEach((name) => {
        const value = rootStyles.getPropertyValue(name).trim();
        if (value) now.style.setProperty(name, value);
      });
      document.body.appendChild(now);
      now.dataset.erPortal = 'true';
    }

    const themeAnimations = new Map();
    const THEME_TRANSITION_MS = reduceMotion ? 1 : 1450;

    const parseRgbVariable = (element, name, fallback) => {
      const raw = window.getComputedStyle(element).getPropertyValue(name).trim();
      const values = raw.split(',').map((part) => Number.parseFloat(part.trim()));
      return values.length === 3 && values.every(Number.isFinite) ? values : fallback;
    };

    const setThemeVariable = (name, value, duration = THEME_TRANSITION_MS) => {
      const target = String(value).split(',').map((part) => Number.parseFloat(part.trim()));
      if (target.length !== 3 || target.some((channel) => !Number.isFinite(channel))) {
        root.style.setProperty(name, value);
        if (now) now.style.setProperty(name, value);
        return;
      }

      const previousFrame = themeAnimations.get(name);
      if (previousFrame) cancelAnimationFrame(previousFrame);

      const start = parseRgbVariable(root, name, target);
      const startedAt = performance.now();
      const ease = (t) => t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const frame = (time) => {
        const progress = Math.min(1, Math.max(0, (time - startedAt) / duration));
        const eased = ease(progress);
        const current = start.map((channel, index) => Math.round(channel + ((target[index] - channel) * eased)));
        const serialized = current.join(',');
        root.style.setProperty(name, serialized);
        if (now) now.style.setProperty(name, serialized);

        if (progress < 1) {
          themeAnimations.set(name, requestAnimationFrame(frame));
        } else {
          themeAnimations.delete(name);
        }
      };

      themeAnimations.set(name, requestAnimationFrame(frame));
    };

    const formatTime = (seconds) => {
      if (!Number.isFinite(seconds) || seconds < 0) return '--:--';
      const whole = Math.floor(seconds);
      return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
    };

    const genreValues = (card) => displayValue(card.dataset.genre).split('||').map((item) => item.trim()).filter(Boolean);

    const uniqueSorted = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const fillSelect = (select, values) => {
      if (!select) return;
      uniqueSorted(values).forEach((value) => {
        const option = document.createElement('option');
        option.value = normalize(value);
        option.textContent = value;
        select.appendChild(option);
      });
    };

    fillSelect(artistFilter, releasedCards.map((card) => displayValue(card.dataset.artist)));
    fillSelect(typeFilter, releasedCards.map((card) => displayValue(card.dataset.type)));
    fillSelect(genreFilter, releasedCards.flatMap(genreValues));

    const currentYear = root.dataset.currentYear || String(new Date().getFullYear());
    const availableYears = [...new Set(releasedCards.map((card) => displayValue(card.dataset.year)).filter(Boolean))]
      .sort((a, b) => Number(b) - Number(a));

    if (yearSelect) {
      yearSelect.replaceChildren();
      const allYears = document.createElement('option');
      allYears.value = '';
      allYears.textContent = 'All years';
      yearSelect.appendChild(allYears);
      availableYears.forEach((year) => {
        const option = document.createElement('option');
        option.value = year;
        option.textContent = year;
        yearSelect.appendChild(option);
      });
      yearSelect.value = '';
    }

    const getFilters = () => ({
      query: normalize(search && search.value),
      artist: normalize(artistFilter && artistFilter.value),
      type: normalize(typeFilter && typeFilter.value),
      genre: normalize(genreFilter && genreFilter.value),
      year: displayValue(yearSelect && yearSelect.value)
    });

    const sortCards = (list) => {
      const sort = sortSelect ? sortSelect.value : 'date-desc';
      return [...list].sort((a, b) => {
        if (sort === 'date-asc' || sort === 'date-desc') {
          const aDate = a.dataset.date ? Date.parse(`${a.dataset.date}T00:00:00`) : 0;
          const bDate = b.dataset.date ? Date.parse(`${b.dataset.date}T00:00:00`) : 0;
          return sort === 'date-asc' ? aDate - bDate : bDate - aDate;
        }
        if (sort === 'title-asc') return displayValue(a.dataset.title).localeCompare(displayValue(b.dataset.title), undefined, { sensitivity: 'base' });
        if (sort === 'artist-asc') return displayValue(a.dataset.artist).localeCompare(displayValue(b.dataset.artist), undefined, { sensitivity: 'base' });
        if (sort === 'price-asc') return (Number(a.dataset.price) || 0) - (Number(b.dataset.price) || 0);
        if (sort === 'price-desc') return (Number(b.dataset.price) || 0) - (Number(a.dataset.price) || 0);
        return 0;
      });
    };

    const buildActiveNote = (filters) => {
      const parts = [];
      if (filters.artist && artistFilter) parts.push(artistFilter.options[artistFilter.selectedIndex]?.textContent || 'Artist');
      if (filters.type && typeFilter) parts.push(typeFilter.options[typeFilter.selectedIndex]?.textContent || 'Type');
      if (filters.genre && genreFilter) parts.push(genreFilter.options[genreFilter.selectedIndex]?.textContent || 'Genre');
      if (filters.year) parts.push(filters.year);
      if (filters.query) parts.push(`“${search.value.trim()}”`);
      return parts.length ? `Filtered by ${parts.join(' · ')}` : '';
    };

    const updateFilterCount = () => {
      const filters = getFilters();
      const count = [filters.artist, filters.type, filters.genre, filters.year].filter(Boolean).length;
      if (!filterCount) return;
      filterCount.hidden = count === 0;
      filterCount.textContent = count;
    };

    const renderUpcoming = () => {
      if (!upcomingSection || !upcomingResults) return;

      const sortedUpcoming = [...upcomingCards].sort((a, b) => {
        const aDate = a.dataset.date ? Date.parse(`${a.dataset.date}T00:00:00`) : Number.MAX_SAFE_INTEGER;
        const bDate = b.dataset.date ? Date.parse(`${b.dataset.date}T00:00:00`) : Number.MAX_SAFE_INTEGER;
        return aDate - bDate;
      });

      upcomingSection.hidden = sortedUpcoming.length === 0;
      if (upcomingCount) {
        upcomingCount.textContent = `${sortedUpcoming.length} scheduled`;
      }

      sortedUpcoming.forEach((card, index) => {
        card.hidden = false;
        card.classList.add('is-upcoming');
        upcomingResults.appendChild(card);
        prepareRowReveal(card, index);
      });
    };

    let currentPage = 1;
    const pageSize = Math.max(12,Number(root.dataset.pageSize)||24);
    const render = (options = {}) => {
      if (!options.keepPage) currentPage = 1;
      const filters = getFilters();
      root.classList.toggle('is-filtering', Boolean(filters.query || filters.artist || filters.type || filters.genre || filters.year));
      const filtered = releasedCards.filter((card) => {
        if (filters.year && displayValue(card.dataset.year) !== filters.year) return false;
        if (filters.query && !normalize(card.dataset.search).includes(filters.query)) return false;
        if (filters.artist && normalize(card.dataset.artist) !== filters.artist) return false;
        if (filters.type && normalize(card.dataset.type) !== filters.type) return false;
        if (filters.genre && !genreValues(card).some((genre) => normalize(genre) === filters.genre)) return false;
        return true;
      });

      const sorted = sortCards(filtered);
      const totalPages = Math.max(1,Math.ceil(sorted.length/pageSize));
      currentPage = Math.min(currentPage,totalPages);
      const startIndex = (currentPage-1)*pageSize;
      const pageCards = sorted.slice(startIndex,startIndex+pageSize);
      const visibleSet = new Set(pageCards);
      drawPages(sorted.length,totalPages);

      releasedCards.forEach((card) => {
        const shouldShow = visibleSet.has(card);
        if (!shouldShow) {
          card.hidden = true;
          card.classList.remove('is-revealed');
          card.dataset.erRevealReady = 'false';
          if (rowRevealObserver) rowRevealObserver.unobserve(card);
          return;
        }
        card.hidden = false;
      });

      pageCards.forEach((card, index) => {
        card.classList.remove('is-upcoming');
        results.appendChild(card);
        const numberEl = card.querySelector('[data-er-track-number]');
        if (numberEl) numberEl.textContent = String(startIndex + index + 1);
        prepareRowReveal(card, index);
      });

      countEls.forEach((countEl) => { countEl.textContent = `${sorted.length} release${sorted.length === 1 ? '' : 's'}`; });
      if (activeNote) activeNote.textContent = buildActiveNote(filters);
      if (empty) empty.hidden = sorted.length !== 0;
      results.hidden = sorted.length === 0;

      const hasChanges = Boolean(filters.query || filters.artist || filters.type || filters.genre || filters.year || (sortSelect && sortSelect.value !== 'date-desc'));
      if (clearButton) clearButton.hidden = !hasChanges;
      updateFilterCount();

      const currentlyPlaying = Boolean(activeAudio && !activeAudio.paused);
      if (!currentlyPlaying && sorted.length && (!activeCard || !sorted.includes(activeCard))) {
        const nextPlayable = sorted.find((card) => card.dataset.audioUrl);
        if (nextPlayable) setActiveCard(nextPlayable, false);
      }
    };

    const drawPages = (count,total) => {
      const nav=root.querySelector('[data-er-pages]');if(!nav)return;nav.hidden=total<=1;nav.replaceChildren();
      const make=(page,text,aria)=>{const button=document.createElement('button');button.type='button';button.textContent=text;button.setAttribute('aria-label',aria);button.disabled=page<1||page>total;if(page===currentPage)button.setAttribute('aria-current','page');button.addEventListener('click',()=>{currentPage=page;render({keepPage:true});const library=root.querySelector('.erdisc__library');library?.focus({preventScroll:true});library?.scrollIntoView({behavior:reduceMotion?'instant':'smooth',block:'start'});});return button;};
      nav.append(make(currentPage-1,'‹',label('previous')));
      const pageNumbers=matchMedia('(max-width:600px)').matches?[1,currentPage,total]:[1,currentPage-1,currentPage,currentPage+1,total];
      [...new Set(pageNumbers)].filter(page=>page>0&&page<=total).sort((a,b)=>a-b).forEach(page=>nav.append(make(page,String(page),`${label('page')} ${page}`)));
      nav.append(make(currentPage+1,'›',label('next')));
      const status=document.createElement('span');status.setAttribute('aria-live','polite');status.textContent=`${(currentPage-1)*pageSize+1}–${Math.min(currentPage*pageSize,count)} / ${count}`;nav.append(status);
    };
    const buildSpotlight = () => {
      const host=root.querySelector('[data-er-spotlight]');if(!host)return;
      const newest=[...releasedCards].sort((a,b)=>(b.dataset.date||'').localeCompare(a.dataset.date||'')||(a.dataset.title||'').localeCompare(b.dataset.title||''));
      host.hidden=!newest.length;if(!newest.length)return;
      const create=(tag,className,text)=>{const node=document.createElement(tag);node.className=className;if(text!==undefined)node.textContent=text;return node;};
      const safe=value=>{if(!value||!String(value).trim()||value==='#')return '';try{const url=new URL(value,location.origin);return ['http:','https:'].includes(url.protocol)?url.href:'';}catch{return '';}};
      const feature=(card)=>{
        const item=create('article','erdisc__new-card');
        const art=create('a','erdisc__new-art');art.href=safe(card.dataset.productUrl)||'#';art.setAttribute('aria-label',card.dataset.title);
        const source=card.querySelector('.erdisc__cover');
        const artworkUrl=safe(card.dataset.imageUrl)||safe(source?.currentSrc)||safe(source?.getAttribute('src'));
        if(artworkUrl){
          const img=document.createElement('img');
          img.className='erdisc__new-cover';img.alt=card.dataset.title||'';
          img.width=192;img.height=192;img.loading='eager';img.decoding='async';
          const url=new URL(artworkUrl);
          if(url.pathname.includes('/cdn/')||url.hostname.endsWith('.shopify.com'))url.searchParams.set('width',String(img.width));
          img.src=url.href;
          img.addEventListener('error',()=>{if(img.src!==artworkUrl)img.src=artworkUrl;},{once:true});
          art.append(img);
        }else if(source){art.append(source.cloneNode(true));}
        item.append(art);
        const original=card.querySelector('[data-er-track-play]');
        if(original){const button=create('button','erdisc__new-play');button.type='button';button.dataset.erSpotlightPlay=card.dataset.productId;button.dataset.title=card.dataset.title;button.disabled=original.disabled;button.setAttribute('aria-label',`Play preview of ${card.dataset.title}`);button.setAttribute('aria-pressed','false');for (const name of ['play','pause']) {const symbol=original.querySelector(`.erdisc__track-${name}-icon`)?.cloneNode(true);if(symbol)button.append(symbol);}button.addEventListener('click',()=>original.click());item.append(button);}
        const copy=create('div','erdisc__new-copy');
        const heading=create('h3','erdisc__new-title');const title=create('a','',card.dataset.title);title.href=safe(card.dataset.productUrl)||'#';title.title=card.dataset.title||'';heading.append(title);copy.append(heading);
        copy.append(create('p','erdisc__new-artist',card.dataset.artist));
        item.append(copy);return item;
      };
      host.replaceChildren(...newest.slice(0,8).map(feature));
      if (!activeAudio || activeAudio.paused) { applyRootPalette(newest[0]); transitionAmbient(newest[0].dataset.imageUrl); }
    };

    const buildTopArtists = () => {
      const section = root.querySelector('[data-er-top-artists]');
      const list = root.querySelector('[data-er-top-artists-list]');
      if (!section || !list) return;
      const paging = root.querySelector('[data-er-pagination-state]');
      if (paging?.dataset.nextUrl && paging.dataset.erCatalogComplete !== 'true') {
        section.hidden = true;
        return;
      }

      const safeUrl = (value, sameOrigin = false) => {
        if (typeof value !== 'string' || !value.trim() || value === '#') return '';
        try {
          const url = new URL(value, location.origin);
          return ['http:', 'https:'].includes(url.protocol) && (!sameOrigin || url.origin === location.origin) ? url.href : '';
        } catch { return ''; }
      };
      let directory = [];
      try {
        const parsed = JSON.parse(root.querySelector('[data-er-artist-directory]')?.textContent || '[]');
        if (Array.isArray(parsed)) directory = parsed;
      } catch {}

      const artists = new Map();
      directory.forEach((artist) => {
        const name = typeof artist?.name === 'string' ? artist.name.trim() : '';
        const key = normalize(name);
        const url = safeUrl(artist?.url, true);
        if (!key || !url || artists.has(key)) return;
        artists.set(key, { name, url, image: safeUrl(artist.image), releases: new Set() });
      });
      releasedCards.forEach((card) => {
        const artist = artists.get(normalize(card.dataset.artist));
        if (artist) artist.releases.add(card.dataset.releasecoreId || card.dataset.productId || card);
      });
      const ranked = [...artists.values()]
        .filter((artist) => artist.releases.size > 0)
        .sort((a, b) => b.releases.size - a.releases.size || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        .slice(0, 10);

      const rows = ranked.map((artist, index) => {
        const create = (tag, className, text) => {
          const node = document.createElement(tag);
          node.className = className;
          if (text !== undefined) node.textContent = text;
          return node;
        };
        const row = create('li', 'erdisc__top-artist');
        const link = create('a', 'erdisc__top-artist-link');
        link.href = artist.url;
        const rank = create('span', 'erdisc__top-artist-rank', String(index + 1).padStart(2, '0'));
        rank.setAttribute('aria-hidden', 'true');
        const avatar = create('span', 'erdisc__top-artist-avatar');
        avatar.setAttribute('aria-hidden', 'true');
        const initial = create('span', 'erdisc__top-artist-initial', artist.name.slice(0, 1).toUpperCase());
        if (artist.image) {
          const image = document.createElement('img');
          image.src = artist.image;
          image.alt = '';
          image.width = 96;
          image.height = 96;
          image.loading = 'lazy';
          image.decoding = 'async';
          image.addEventListener('error', () => avatar.replaceChildren(initial), { once: true });
          avatar.append(image);
        } else avatar.append(initial);
        const name = create('span', 'erdisc__top-artist-name', artist.name);
        name.title = artist.name;
        const count = artist.releases.size;
        const metric = create('span', 'erdisc__top-artist-metric', String(count));
        const metricLabel = `${count} ${label(count === 1 ? 'release' : 'releases')}`;
        metric.title = metricLabel;
        link.setAttribute('aria-label', `${artist.name}, ${metricLabel}`);
        link.append(rank, avatar, name, metric);
        row.append(link);
        return row;
      });
      list.replaceChildren(...rows);
      section.hidden = rows.length === 0;
    };

    const chartLimit = clamp(Number.parseInt(window.getComputedStyle(root).getPropertyValue('--er-chart-limit'), 10) || 5, 3, 8);
    let activeChartMode = 'ytd';
    const remoteCharts = { ytd: [], lifetime: [] };
    const virtualChartCards = new Map();

    const formatCompactNumber = (value) => {
      const number = numericValue(value);
      if (!number) return '';
      try {
        return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(number);
      } catch (error) {
        return number.toLocaleString();
      }
    };

    const getManualChartIds = (mode) => [
      ...root.querySelectorAll(`[data-er-chart-manual="${mode}"] [data-product-id]`)
    ].map((node) => node.dataset.productId).filter(Boolean);

    const findChartCard = (item) => {
      if (!item?.standalone) return null;
      return releasedCards.find((card) =>
        (item.releaseId && card.dataset.releasecoreId === String(item.releaseId)) ||
        (item.releaseProductNumericId && card.dataset.productId === String(item.releaseProductNumericId)) ||
        (item.productNumericId && card.dataset.productId === String(item.productNumericId))
      ) || null;
    };

    const chartFeaturedText = (item) => (Array.isArray(item?.featuredArtists) ? item.featuredArtists : [])
      .map((artist) => displayValue(artist?.name || artist))
      .filter(Boolean)
      .join(', ');

    const ensureRemoteChartCard = (item) => {
      const catalogCard = findChartCard(item);
      if (catalogCard) return catalogCard;

      const key = String(item?.trackId || item?.releaseId || item?.productNumericId || '');
      if (!key) return null;
      if (virtualChartCards.has(key)) return virtualChartCards.get(key);

      const card = document.createElement('article');
      card.dataset.productId = `chart-${key}`;
      card.dataset.releasecoreId = String(item.releaseId || '');
      card.dataset.title = displayValue(item.title);
      card.dataset.artist = displayValue(item.artist);
      card.dataset.featured = chartFeaturedText(item);
      card.dataset.type = 'Track';
      card.dataset.productUrl = displayValue(item.productUrl) || '#';
      card.dataset.imageUrl = displayValue(item.coverUrl);
      card.dataset.audioUrl = displayValue(item.previewUrl);
      card.dataset.search = normalize(`${card.dataset.title} ${card.dataset.artist} ${card.dataset.featured}`);
      card.hidden = true;

      if (card.dataset.audioUrl) {
        const button = document.createElement('button');
        button.type = 'button';
        button.hidden = true;
        button.dataset.erTrackPlay = '';
        button.setAttribute('aria-label', `Play preview of ${card.dataset.title || 'track'}`);

        const playIcon = document.createElement('span');
        playIcon.className = 'erdisc__track-play-icon';
        playIcon.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.35v13.3L18.5 12 8 5.35Z" fill="currentColor"></path></svg>';
        const pauseIcon = document.createElement('span');
        pauseIcon.className = 'erdisc__track-pause-icon';
        pauseIcon.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7V5Zm6 0h4v14h-4V5Z" fill="currentColor"></path></svg>';
        button.append(playIcon, pauseIcon);

        const audio = document.createElement('audio');
        audio.dataset.erTrackAudio = '';
        audio.preload = 'none';
        audio.src = card.dataset.audioUrl;
        card.append(button, audio);
      }

      virtualChartCards.set(key, card);
      cards.push(card);
      bindPlayback(card);
      return card;
    };

    const applyRemoteChartData = () => {
      ['ytd', 'lifetime'].forEach((mode) => {
        const dataKey = mode === 'lifetime' ? 'playsLifetime' : 'playsYtd';
        remoteCharts[mode].forEach((item) => {
          const card = ensureRemoteChartCard(item);
          if (card && numericValue(item.plays)) card.dataset[dataKey] = String(item.plays);
        });
      });
    };

    const getChartCards = (mode) => {
      const manualIds = getManualChartIds(mode);
      if (manualIds.length) {
        const byId = new Map(releasedCards.map((card) => [card.dataset.productId, card]));
        return manualIds.map((id) => byId.get(id)).filter(Boolean).slice(0, chartLimit);
      }

      const remote = remoteCharts[mode].map(ensureRemoteChartCard).filter(Boolean);
      if (remote.length) return remote.slice(0, chartLimit);

      const dataKey = mode === 'lifetime' ? 'playsLifetime' : 'playsYtd';
      return releasedCards
        .filter((card) => normalize(card.dataset.type) === 'single')
        .map((card) => ({ card, plays: numericValue(card.dataset[dataKey]) }))
        .filter((entry) => entry.plays > 0)
        .sort((a, b) => b.plays - a.plays || (b.card.dataset.date || '').localeCompare(a.card.dataset.date || ''))
        .slice(0, chartLimit)
        .map((entry) => entry.card);
    };

    const renderChart = (requestedMode = activeChartMode) => {
      if (!charts || !chartList) return;

      const available = {
        ytd: getChartCards('ytd'),
        lifetime: getChartCards('lifetime')
      };
      if (!available.ytd.length && !available.lifetime.length) {
        if (charts.dataset.erChartPending === 'true') {
          charts.hidden = false;
          return;
        }
        charts.hidden = true;
        charts.setAttribute('aria-busy', 'false');
        chartList.replaceChildren();
        return;
      }

      charts.dataset.erChartPending = 'false';
      charts.setAttribute('aria-busy', 'false');
      activeChartMode = available[requestedMode]?.length
        ? requestedMode
        : (available.ytd.length ? 'ytd' : 'lifetime');

      charts.hidden = false;
      chartTabs.forEach((tab) => {
        const mode = tab.dataset.erChartTab;
        const hasRows = Boolean(available[mode]?.length);
        const active = mode === activeChartMode;
        tab.hidden = !hasRows;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      const cardsForMode = available[activeChartMode];
      const metricKey = activeChartMode === 'lifetime' ? 'playsLifetime' : 'playsYtd';
      const fragment = document.createDocumentFragment();

      cardsForMode.forEach((card, index) => {
        const row = document.createElement('article');
        row.className = 'erdisc__chart-row';

        const rank = document.createElement('span');
        rank.className = 'erdisc__chart-rank';
        rank.textContent = String(index + 1).padStart(2, '0');
        row.append(rank);

        const artLink = document.createElement('a');
        artLink.className = 'erdisc__chart-art';
        artLink.href = card.dataset.productUrl || '#';
        artLink.setAttribute('aria-label', `View ${card.dataset.title || 'release'}`);
        const imageUrl = card.dataset.imageUrl || '';
        if (imageUrl) {
          const image = document.createElement('img');
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          try {
            const url = new URL(imageUrl, location.origin);
            if (url.pathname.includes('/cdn/') || url.hostname.endsWith('.shopify.com')) url.searchParams.set('width', '128');
            image.src = url.href;
          } catch (error) {
            image.src = imageUrl;
          }
          artLink.append(image);
        }
        row.append(artLink);

        const copy = document.createElement('div');
        copy.className = 'erdisc__chart-copy';
        const title = document.createElement('a');
        title.className = 'erdisc__chart-title';
        title.href = card.dataset.productUrl || '#';
        const chartTitle = card.dataset.title || '';
        title.textContent = card.dataset.featured && !/\(feat\./i.test(chartTitle)
          ? `${chartTitle} (feat. ${card.dataset.featured})`
          : chartTitle;
        const artist = document.createElement('span');
        artist.className = 'erdisc__chart-artist';
        artist.textContent = card.dataset.artist || '';
        copy.append(title, artist);
        row.append(copy);

        const tail = document.createElement('div');
        tail.className = 'erdisc__chart-tail';
        const metric = formatCompactNumber(card.dataset[metricKey]);
        if (metric) {
          const metricNode = document.createElement('span');
          metricNode.className = 'erdisc__chart-metric';
          metricNode.textContent = `${metric} plays`;
          tail.append(metricNode);
        }

        const original = card.querySelector('[data-er-track-play]');
        if (original) {
          const play = document.createElement('button');
          play.type = 'button';
          play.className = 'erdisc__chart-play';
          play.dataset.erChartPlay = card.dataset.productId || '';
          play.dataset.title = card.dataset.title || '';
          play.setAttribute('aria-label', `Play preview of ${card.dataset.title || 'release'}`);
          play.setAttribute('aria-pressed', 'false');
          for (const name of ['play', 'pause']) {
            const symbol = original.querySelector(`.erdisc__track-${name}-icon`)?.cloneNode(true);
            if (symbol) play.append(symbol);
          }
          play.addEventListener('click', () => original.click());
          tail.append(play);
        }

        row.append(tail);
        fragment.append(row);
      });

      chartList.replaceChildren(fragment);
      syncPlayingState();
    };

    const buildCharts = () => {
      if (!charts) return;
      chartTabs.forEach((tab) => {
        if (tab.dataset.erChartBound === 'true') return;
        tab.dataset.erChartBound = 'true';
        tab.addEventListener('click', () => {
          activeChartMode = tab.dataset.erChartTab || 'ytd';
          renderChart(activeChartMode);
        });
      });
      renderChart(activeChartMode);
    };

    const hydratePublicCharts = async () => {
      if (!charts || typeof fetch !== 'function') return;
      try {
        const response = await fetch(`/apps/releasecore/music/charts?limit=${chartLimit}`, {
          signal: AbortSignal.any([controller.signal, AbortSignal.timeout(12000)]),
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) {
          charts.dataset.erChartPending = 'false';
          renderChart(activeChartMode);
          return;
        }
        const payload = await response.json();
        if (!payload?.ok) {
          charts.dataset.erChartPending = 'false';
          renderChart(activeChartMode);
          return;
        }
        charts.dataset.erChartPending = 'false';
        remoteCharts.ytd = Array.isArray(payload.ytd) ? payload.ytd : [];
        remoteCharts.lifetime = Array.isArray(payload.lifetime) ? payload.lifetime : [];
        const yearTab = chartTabs.find((tab) => tab.dataset.erChartTab === 'ytd');
        if (yearTab && payload.year) yearTab.textContent = String(payload.year);
        applyRemoteChartData();
        renderChart(activeChartMode);
      } catch (error) {
        if (charts) {
          charts.dataset.erChartPending = 'false';
          renderChart(activeChartMode);
        }
        if (!disposed && error?.name !== 'AbortError' && error?.name !== 'TimeoutError') {
          console.warn('East Rock Music: chart data unavailable; using Shopify chart fallbacks.', error);
        }
      }
    };

    const updateDiscoverSummary = () => {
      if (summaryReleases) summaryReleases.textContent = `${releasedCards.length} release${releasedCards.length === 1 ? '' : 's'}`;
      if (summaryArtists) {
        const artistCount = root.querySelectorAll('.erdisc__artist').length;
        summaryArtists.textContent = artistCount ? `${artistCount} artist${artistCount === 1 ? '' : 's'}` : '';
      }
      if (summaryUpcoming) {
        summaryUpcoming.textContent = upcomingCards.length ? `${upcomingCards.length} upcoming` : '';
      }
    };

    const clearFilters = () => {
      if (search) search.value = '';
      if (artistFilter) artistFilter.value = '';
      if (typeFilter) typeFilter.value = '';
      if (genreFilter) genreFilter.value = '';
      if (yearSelect) yearSelect.value = '';
      if (sortSelect) sortSelect.value = 'date-desc';
      render();
    };

    if (filterToggle && filterPanel) {
      filterToggle.addEventListener('click', () => {
        const open = filterToggle.getAttribute('aria-expanded') !== 'true';
        filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        filterPanel.dataset.open = open ? 'true' : 'false';
      });
    }

    [artistFilter, typeFilter, genreFilter, sortSelect, yearSelect].forEach((control) => {
      if (control) control.addEventListener('change', render);
    });
    if (search) {
      let timer;
      search.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(render, 80);
      });
    }
    if (clearButton) clearButton.addEventListener('click', clearFilters);
    if (emptyReset) emptyReset.addEventListener('click', clearFilters);

    const soften = ([r, g, b]) => {
      const lum = luminance([r, g, b]);
      let amount = .025;
      const target = 154;
      if (lum < 48) amount = .18;
      else if (lum > 220) amount = .11;

      const softened = [
        clamp((r * (1 - amount)) + (target * amount), 24, 238),
        clamp((g * (1 - amount)) + (target * amount), 24, 238),
        clamp((b * (1 - amount)) + (target * amount), 24, 238)
      ];
      const softenedLum = luminance(softened);

      return softened.map((channel) =>
        Math.round(clamp(softenedLum + ((channel - softenedLum) * 1.28), 24, 240))
      );
    };

    const extractPalette = (image) => {
      const canvas = document.createElement('canvas');
      const size = 36;
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;
      context.drawImage(image, 0, 0, size, size);
      const pixels = context.getImageData(0, 0, size, size).data;
      const buckets = new Map();

      for (let i = 0; i < pixels.length; i += 4) {
        if (pixels[i + 3] < 180) continue;
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const saturation = max === 0 ? 0 : (max - min) / max;
        const lum = luminance([r, g, b]);
        if (lum < 15 || lum > 243) continue;
        const key = `${Math.round(r / 32) * 32},${Math.round(g / 32) * 32},${Math.round(b / 32) * 32}`;
        const weight = .38 + (saturation * 2.05);
        const bucket = buckets.get(key) || { score: 0, count: 0, r: 0, g: 0, b: 0 };
        bucket.score += weight;
        bucket.count += 1;
        bucket.r += r;
        bucket.g += g;
        bucket.b += b;
        buckets.set(key, bucket);
      }

      const ranked = [...buckets.values()]
        .filter((bucket) => bucket.count >= 2)
        .map((bucket) => ({
          score: bucket.score,
          color: [
            Math.round(bucket.r / bucket.count),
            Math.round(bucket.g / bucket.count),
            Math.round(bucket.b / bucket.count)
          ]
        }))
        .sort((a, b) => b.score - a.score);

      if (!ranked.length) return null;
      const chromatic=ranked.filter(({color,score})=>{
        const high=Math.max(...color),low=Math.min(...color);
        return high-low>28&&(high-low)/Math.max(1,high)>.22&&score>=ranked[0].score*.08;
      });
      const primary = soften((chromatic[0]||ranked[0]).color);
      let secondary = primary;
      for (const candidate of [...chromatic,...ranked]) {
        const color = soften(candidate.color);
        if (distance(primary, color) > 66) {
          secondary = color;
          break;
        }
      }
      return { primary, secondary };
    };

    const applyCardPalette = (card, palette) => {
      if (!palette) return;
      let primary = palette.primary;
      let secondary = palette.secondary;
      if (luminance(primary) < 72) primary = mix(primary, [255,255,255], .26);
      if (luminance(primary) > 220) primary = mix(primary, [0,0,0], .12);


      primary = mix(primary, [150, 152, 156], .035);
      secondary = mix(secondary, primary, .10);

      card.style.setProperty('--er-row-accent', primary.join(','));
      card.style.setProperty('--er-row-accent-2', secondary.join(','));
      card.dataset.erPalette = `${primary.join('-')}|${secondary.join('-')}`;
    };

    const paletteLoads = new Map();
    let paletteRequest = 0;
    const loadPalette = (card) => {
      const source=card.dataset.imageUrl;
      if(!source)return Promise.resolve(null);
      if(paletteLoads.has(source))return paletteLoads.get(source);
      const pending=new Promise(resolve=>{
        const image=new Image();image.crossOrigin='anonymous';image.decoding='async';
        image.onload=()=>{try{resolve(extractPalette(image));}catch{resolve(null);}};
        image.onerror=()=>resolve(null);
        const url=new URL(source,location.origin);
        if(url.pathname.includes('/cdn/')||url.hostname.endsWith('.shopify.com'))url.searchParams.set('width','96');
        image.src=url.href;
      });
      paletteLoads.set(source,pending);
      return pending;
    };
    const applyRootPalette = async (card) => {
      const request=++paletteRequest;
      if(!card)return;
      if(!card.dataset.erPalette){
        const palette=await loadPalette(card);
        if(disposed||request!==paletteRequest)return;
        if(palette)applyCardPalette(card,palette);
      }
      if(disposed||request!==paletteRequest||!card.dataset.erPalette)return;
      const [p,s]=card.dataset.erPalette.split('|');
      const primary=p.split('-').map(Number);
      setThemeVariable('--er-accent',primary.join(','));
      setThemeVariable('--er-accent-2',s.split('-').join(','));
      setThemeVariable('--er-fg',mix([250,249,253],primary,.12).join(','));
      setThemeVariable('--er-muted',mix([218,218,226],primary,.20).join(','));
      setThemeVariable('--er-highlight',mix([255,250,255],primary,.28).join(','));
    };

    const sampleCard = (card) => {
      if (card.dataset.erPaletteReady === 'true') return;
      const image = card.querySelector('.erdisc__cover:not(.erdisc__cover--placeholder)');
      if (!image) return;
      const run = () => {
        try {
          const palette = extractPalette(image);
          if (palette) applyCardPalette(card, palette);
          card.dataset.erPaletteReady = 'true';
        } catch (error) {
          card.dataset.erPaletteReady = 'failed';
        }
      };
      if (image.complete && image.naturalWidth > 0) run();
      else image.addEventListener('load', run, { once: true });
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          sampleCard(entry.target);
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '240px 0px' });
      paletteObserver = observer;
      cards.forEach((card) => observer.observe(card));
    } else {
      cards.forEach(sampleCard);
    }

    let ambientFront = 'a';
    let ambientRequest = 0;
    const transitionAmbient = (imageUrl) => {
      if (!imageUrl || !ambientA || !ambientB) return;
      const requestId = ++ambientRequest;
      const preload = new Image();
      preload.onload = () => {
        if (requestId !== ambientRequest) return;
        const next = ambientFront === 'a' ? ambientB : ambientA;
        const current = ambientFront === 'a' ? ambientA : ambientB;
        next.classList.remove('is-visible');
        next.style.backgroundImage = `url("${imageUrl.replace(/"/g, '%22')}")`;
        void next.offsetWidth;
        requestAnimationFrame(() => {
          next.classList.add('is-visible');
          current.classList.remove('is-visible');
          ambientFront = ambientFront === 'a' ? 'b' : 'a';
        });
      };
      preload.src = imageUrl;
    };

    const initialYear = yearSelect ? yearSelect.value : '';
    const playableCards = [
      ...releasedCards.filter((card) => card.dataset.audioUrl),
      ...upcomingCards.filter((card) => card.dataset.audioUrl)
    ].sort((a, b) => {
      const aUpcoming = isUpcomingCard(a);
      const bUpcoming = isUpcomingCard(b);
      if (aUpcoming !== bUpcoming) return aUpcoming ? 1 : -1;
      const aDate = a.dataset.date ? Date.parse(`${a.dataset.date}T00:00:00`) : 0;
      const bDate = b.dataset.date ? Date.parse(`${b.dataset.date}T00:00:00`) : 0;
      return aUpcoming ? aDate - bDate : bDate - aDate;
    });
    let activeCard =
      playableCards.find((card) => (!initialYear || card.dataset.year === initialYear) && card.classList.contains('is-active')) ||
      playableCards.find((card) => !initialYear || card.dataset.year === initialYear) ||
      playableCards[0] ||
      null;
    let activeAudio = activeCard ? activeCard.querySelector('[data-er-track-audio]') : null;

    const getPreviewDuration = (audio) => {
      if (!audio) return PREVIEW_LIMIT_SECONDS;
      const sourceDuration = Number.isFinite(audio.duration) ? audio.duration : 0;
      return sourceDuration > 0 ? Math.min(sourceDuration, PREVIEW_LIMIT_SECONDS) : PREVIEW_LIMIT_SECONDS;
    };

    const syncPlayerUI = () => {
      if (!now || !activeAudio || !heroSeek || !heroTime) return;
      const duration = getPreviewDuration(activeAudio);
      const current = Math.min(Number.isFinite(activeAudio.currentTime) ? activeAudio.currentTime : 0, duration);
      const progress = duration > 0 ? clamp((current / duration) * 100, 0, 100) : 0;
      heroSeek.value = progress.toFixed(2);
      now.style.setProperty('--er-preview-progress', `${progress}%`);
      heroTime.textContent = `${formatTime(current)} / ${formatTime(duration)}`;
    };

    const syncPlayingState = () => {
      const playing = Boolean(activeAudio && !activeAudio.paused);
      root.classList.toggle('is-playing', playing);
      if (now) {now.classList.toggle('is-playing', playing);if(playing)now.hidden=false;}
      root.querySelectorAll('[data-er-spotlight-play]').forEach(button=>{const on=playing&&button.dataset.erSpotlightPlay===activeCard?.dataset.productId;button.setAttribute('aria-pressed',String(on));button.classList.toggle('is-playing',on);button.setAttribute('aria-label',`${on?'Pause':'Play'} preview of ${button.dataset.title}`);});
      root.querySelectorAll('[data-er-chart-play]').forEach(button=>{const on=playing&&button.dataset.erChartPlay===activeCard?.dataset.productId;button.setAttribute('aria-pressed',String(on));button.classList.toggle('is-playing',on);button.setAttribute('aria-label',`${on?'Pause':'Play'} preview of ${button.dataset.title}`);});
      cards.forEach((card) => card.classList.toggle('is-playing', card === activeCard && playing));
      cards.forEach((card) => {
        const button = card.querySelector('[data-er-track-play]');
        if (!button) return;
        const isPlaying = card === activeCard && playing;
        button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
        button.setAttribute('aria-label', `${isPlaying ? 'Pause' : 'Play'} preview of ${card.dataset.title}`);
      });
      if (heroToggle) {
        heroToggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
        heroToggle.setAttribute('aria-label', playing ? 'Pause preview' : 'Play preview');
      }
      if (heroState) heroState.textContent = playing ? 'Playing' : 'Preview ready';
    };

    const resetAudio = (audio) => {
      if (!audio) return;
      audio.pause();
      try { audio.currentTime = 0; } catch (error) {}
    };

    const updateHeroFromCard = (card) => {
      if (!card || !now) return;
      if (heroTitle) heroTitle.textContent = card.dataset.title || '';
      if (heroArtist) heroArtist.textContent = card.dataset.artist || '';
      if (heroFeatured) {
        const featured = card.dataset.featured || '';
        heroFeatured.hidden = !featured;
        heroFeatured.textContent = featured ? ` · feat. ${featured}` : '';
      }
      if (heroMeta) {
        heroMeta.replaceChildren();
        [card.dataset.type, card.dataset.dateLabel, card.dataset.priceLabel].filter(Boolean).forEach((value) => {
          const span = document.createElement('span');
          span.textContent = value;
          heroMeta.appendChild(span);
        });
      }
      heroProductLinks.forEach((link) => { link.href = card.dataset.productUrl || '#'; });
      if (heroStream) {
        const streamingUrl = card.dataset.streamingUrl || '';
        const preSaveUrl = card.dataset.preSaveUrl || '';
        const destination = streamingUrl || preSaveUrl;
        heroStream.hidden = !destination;
        if (destination) heroStream.href = destination;
        heroStream.setAttribute('aria-label', streamingUrl ? 'Stream release' : 'Pre-save release');
        heroStream.setAttribute('title', streamingUrl ? 'Stream release' : 'Pre-save release');
      }
      const imageUrl = card.dataset.imageUrl || '';
      if (heroArt && imageUrl && heroArt.src !== imageUrl) {
        const preload = new Image();
        preload.onload = () => {
          heroArt.classList.add('is-changing');
          window.setTimeout(() => {
            heroArt.src = imageUrl;
            heroArt.removeAttribute('srcset');
            heroArt.removeAttribute('sizes');
            heroArt.alt = card.dataset.title || '';
            requestAnimationFrame(() => {
              requestAnimationFrame(() => heroArt.classList.remove('is-changing'));
            });
          }, 210);
        };
        preload.src = imageUrl;
      }
      if(activeAudio && !activeAudio.paused){transitionAmbient(imageUrl);applyRootPalette(card);}
    };

    const setActiveCard = (card, playAfter = false) => {
      if (!card || !card.dataset.audioUrl) return;
      const nextAudio = card.querySelector('[data-er-track-audio]');
      if (!nextAudio) return;

      if (activeAudio && activeAudio !== nextAudio) resetAudio(activeAudio);
      cards.forEach((item) => item.classList.toggle('is-active', item === card));
      activeCard = card;
      activeAudio = nextAudio;
      updateHeroFromCard(card);
      syncPlayerUI();
      syncPlayingState();

      if (playAfter) {
        const duration = getPreviewDuration(activeAudio);
        if (activeAudio.currentTime >= duration - .05) activeAudio.currentTime = 0;
        activeAudio.play().catch(() => syncPlayingState());
      }
    };

    const boundCards = new WeakSet();
    const bindPlayback = (card) => {
      if (boundCards.has(card)) return;
      boundCards.add(card);
      const button = card.querySelector('[data-er-track-play]');
      const audio = card.querySelector('[data-er-track-audio]');
      if (!button || !audio) return;

      button.addEventListener('click', () => {
        if (card === activeCard) {
          if (audio.paused) audio.play().catch(() => syncPlayingState());
          else audio.pause();
        } else {
          setActiveCard(card, true);
        }
      });

      audio.addEventListener('loadedmetadata', () => { if (audio === activeAudio) syncPlayerUI(); });
      audio.addEventListener('durationchange', () => { if (audio === activeAudio) syncPlayerUI(); });
      audio.addEventListener('timeupdate', () => {
        if (audio !== activeAudio) return;
        const duration = getPreviewDuration(audio);
        if (audio.currentTime >= duration - .05) {
          resetAudio(audio);
          syncPlayerUI();
          syncPlayingState();
          return;
        }
        syncPlayerUI();
      });
      audio.addEventListener('play', () => {
        transitionAmbient(card.dataset.imageUrl);applyRootPalette(card);
        document.querySelectorAll('audio,video').forEach(media=>{if(media!==audio&&!media.paused)media.pause();});
        cards.forEach((otherCard) => {
          if (otherCard === card) return;
          const otherAudio = otherCard.querySelector('[data-er-track-audio]');
          if (otherAudio && !otherAudio.paused) resetAudio(otherAudio);
        });
        if (card !== activeCard) setActiveCard(card, false);
        syncPlayingState();
      });
      audio.addEventListener('pause', syncPlayingState);
      audio.addEventListener('ended', () => {
        resetAudio(audio);
        syncPlayerUI();
        syncPlayingState();
      });
      audio.addEventListener('error', () => {
        button.disabled = true;
        button.setAttribute('aria-label', 'Audio preview unavailable');
        root.querySelectorAll('[data-er-spotlight-play]').forEach(control=>{if(control.dataset.erSpotlightPlay===card.dataset.productId){control.disabled=true;control.setAttribute('aria-label','Audio preview unavailable');}});
        if (audio === activeAudio && heroState) heroState.textContent = 'Preview unavailable';
      });
    };
    cards.forEach(bindPlayback);

    if (heroToggle) {
      heroToggle.addEventListener('click', () => {
        if (!activeAudio) return;
        if (activeAudio.paused) {
          const duration = getPreviewDuration(activeAudio);
          if (activeAudio.currentTime >= duration - .05) activeAudio.currentTime = 0;
          activeAudio.play().catch(() => syncPlayingState());
        } else {
          activeAudio.pause();
        }
      });
    }

    if (heroSeek) {
      heroSeek.addEventListener('input', () => {
        if (!activeAudio) return;
        const duration = getPreviewDuration(activeAudio);
        activeAudio.currentTime = (clamp(Number(heroSeek.value) || 0, 0, 100) / 100) * duration;
        syncPlayerUI();
      });
    }

    if (activeCard) {
      updateHeroFromCard(activeCard);
      syncPlayerUI();
      syncPlayingState();
    }


    root.addEventListener('aura:music:dispose', () => {
      if(activeAudio)resetAudio(activeAudio);
      if(now)now.remove();
      rowRevealObserver?.disconnect();
      paletteObserver?.disconnect();
      themeAnimations.forEach(frame=>cancelAnimationFrame(frame));
    }, {once:true});

    const refreshCatalog = () => {
      const known = new Set(cards.map(card => card.dataset.productId));
      root.querySelectorAll('[data-er-release]').forEach(card => {
        if (known.has(card.dataset.productId)) return;
        known.add(card.dataset.productId);
        cards.push(card);
        (isUpcomingCard(card) ? upcomingCards : releasedCards).push(card);
        bindPlayback(card);
        sampleCard(card);
      });
      [[artistFilter, releasedCards.map(card => displayValue(card.dataset.artist))],
       [typeFilter, releasedCards.map(card => displayValue(card.dataset.type))],
       [genreFilter, releasedCards.flatMap(genreValues)]].forEach(([select, values]) => {
        if (!select) return;
        const value = select.value;
        while (select.options.length > 1) select.remove(1);
        fillSelect(select, values);
        select.value = value;
      });
      if (yearSelect) {
        const value = yearSelect.value;
        while (yearSelect.options.length > 1) yearSelect.remove(1);
        [...new Set(releasedCards.map(card => card.dataset.year).filter(Boolean))].sort().reverse().forEach(year => {
          const option = document.createElement('option');
          option.value = year;
          option.textContent = year;
          yearSelect.append(option);
        });
        yearSelect.value = value;
      }
      if (!activeCard) {
        const first = releasedCards.find(card => card.dataset.audioUrl) || upcomingCards.find(card => card.dataset.audioUrl);
        if (first) setActiveCard(first);
      }
      buildSpotlight();
      buildTopArtists();
      applyRemoteChartData();
      buildCharts();
      renderUpcoming();
      updateDiscoverSummary();
      render({ keepPage: true });
      syncPlayingState();
    };
    buildSpotlight();
    buildTopArtists();
    buildCharts();
    renderUpcoming();
    updateDiscoverSummary();
    render();
    hydratePublicCharts();
    hydrateCatalog();


    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.add('is-page-ready');
        if (now) now.classList.add('is-dock-ready');
      });
    });
  };
  const scan=(scope)=>{scope.querySelectorAll?.('.aura-music-page').forEach(root=>initialize(root).catch(()=>{root.classList.add('is-page-ready');}));};
  scan(document);
  if(!window.auraMusicSectionListener){window.auraMusicSectionListener=true;document.addEventListener('shopify:section:load',event=>scan(event.target));}
})();
